import { ChevronRight, Copy } from "lucide-react";
import { toast } from "sonner";

import { cn } from "@/lib/utils";

/* Read-only building blocks shared by the intents review pages. */

export function Chip({ children }: { children: React.ReactNode }) {
  return (
    <span className="text-muted-foreground inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-widest">
      {children}
    </span>
  );
}

export function CopyableValue({
  label,
  value,
}: {
  label: string;
  value: string | null;
}) {
  if (!value) return <>—</>;
  return (
    <button
      type="button"
      aria-label={`Copy ${label}`}
      title={`${value}\nClick to copy`}
      onClick={() => {
        navigator.clipboard.writeText(value).then(
          () => toast.success(`${label} copied`),
          () => toast.error("Copy failed"),
        );
      }}
      className="group/copy hover:text-foreground flex w-full min-w-0 items-center gap-1 text-left transition-colors"
    >
      <span className="truncate">{value}</span>
      <Copy className="size-3 shrink-0 opacity-40 transition-opacity group-hover/copy:opacity-100" />
    </button>
  );
}
/** Editorial section label: small caps with a hairline rule running out. */
export function SectionHeading({
  children,
  note,
  action,
}: {
  children: React.ReactNode;
  note?: string;
  action?: React.ReactNode;
}) {
  return (
    <h3 className="text-muted-foreground flex items-center gap-3 text-[11px] font-semibold uppercase tracking-[0.14em]">
      <span className="shrink-0">{children}</span>
      {note && (
        <span className="text-muted-foreground shrink-0 font-normal normal-case italic tracking-normal">
          {note}
        </span>
      )}
      <span aria-hidden className="border-border/70 flex-1 border-t" />
      {action && (
        <span className="shrink-0 font-normal normal-case tracking-normal">
          {action}
        </span>
      )}
    </h3>
  );
}

export function GuidanceProse({
  title,
  text,
  note,
  action,
}: {
  title: string;
  text: string;
  note?: string;
  action?: React.ReactNode;
}) {
  return (
    <section className="space-y-2">
      <SectionHeading note={note} action={action}>
        {title}
      </SectionHeading>
      <p className="text-foreground/85 whitespace-pre-wrap break-words text-sm leading-relaxed">
        {text}
      </p>
    </section>
  );
}

export function GuidanceList({
  title,
  items,
  tone = "default",
  note,
}: {
  title: string;
  items: string[];
  tone?: "default" | "negative";
  note?: string;
}) {
  if (items.length === 0) return null;
  // LLM-derived clauses can repeat; suffix duplicates for stable keys.
  const seen = new Map<string, number>();
  const entries = items.map((item) => {
    const n = seen.get(item) ?? 0;
    seen.set(item, n + 1);
    return { item, key: n === 0 ? item : `${item}~${String(n)}` };
  });
  return (
    <section className="space-y-2">
      <SectionHeading note={note}>{title}</SectionHeading>
      <ul className="space-y-1.5 text-sm leading-relaxed">
        {entries.map(({ item, key }) => (
          <li key={key} className="text-foreground/85 flex gap-2.5">
            <span
              aria-hidden
              className={cn(
                "shrink-0 select-none font-mono text-xs leading-6",
                tone === "negative"
                  ? "text-destructive/70"
                  : "text-muted-foreground/70",
              )}
            >
              {tone === "negative" ? "✕" : "—"}
            </span>
            <span className="min-w-0 break-words">{item}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}
export function JsonDisclosure({
  title,
  value,
  defaultOpen = false,
}: {
  title: string;
  value: unknown;
  defaultOpen?: boolean;
}) {
  return (
    <details open={defaultOpen} className="group">
      <summary className="text-muted-foreground flex cursor-pointer select-none items-center gap-3 text-[11px] font-semibold uppercase tracking-[0.14em] [&::-webkit-details-marker]:hidden">
        <span className="flex shrink-0 items-center gap-1">
          <ChevronRight className="size-3 transition-transform group-open:rotate-90" />
          {title}
        </span>
        <span aria-hidden className="border-border/70 flex-1 border-t" />
      </summary>
      <pre className="bg-muted/30 text-foreground/80 mt-2 overflow-x-auto rounded-lg border p-3 font-mono text-xs leading-relaxed">
        {JSON.stringify(value, null, 2)}
      </pre>
    </details>
  );
}
